'use client';

import { useEffect, useState } from 'react';
import { trackingManager } from '@/lib/tracking/tracking-manager-instance';
import { isChunkLoadError, reloadOnceForChunkError } from '@/lib/utils/chunk-error';

// global-error replaces the root layout, so there is no intl provider here.
const DICTIONARY = {
  en: {
    title: 'Something went wrong',
    description: "We've logged this error and will look into it.",
    tryAgain: 'Try again',
  },
  pt: {
    title: 'Algo correu mal',
    description: 'Registámos este erro e vamos analisá-lo.',
    tryAgain: 'Tentar novamente',
  },
  de: {
    title: 'Etwas ist schiefgelaufen',
    description: 'Wir haben diesen Fehler protokolliert und werden ihn prüfen.',
    tryAgain: 'Erneut versuchen',
  },
} as const;

type Locale = keyof typeof DICTIONARY;

// On the server this is always 'en', and that is what the prerendered page writes into
// <html lang>, so hydrating it reads 'en' back. After a client-side error it runs before
// the first commit, while <html> still carries the page's language.
function pickLocale(): Locale {
  if (typeof document === 'undefined') return 'en';
  const candidates = [document.documentElement.lang, navigator.language];
  for (const candidate of candidates) {
    const code = candidate?.toLowerCase().split('-')[0];
    if (code && code in DICTIONARY) return code as Locale;
  }
  return 'en';
}

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  // Lazy initial state, not an effect: once committed, lang={locale} overwrites <html lang>.
  const [locale] = useState<Locale>(pickLocale);
  const isChunkError = isChunkLoadError(error);

  useEffect(() => {
    // Capture the error with PostHog
    trackingManager.captureException(error, {
      error_boundary: 'global',
      error_digest: error.digest,
      timestamp: new Date().toISOString(),
    });
  }, [error]);

  useEffect(() => {
    // A rejected chunk stays rejected until the document reloads, so reset() cannot recover.
    if (isChunkError) reloadOnceForChunkError();
  }, [isChunkError]);

  const text = DICTIONARY[locale];

  return (
    <html lang={locale}>
      <body>
        <div className="min-h-safe-screen flex items-center justify-center bg-gray-50">
          <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-6">
            <h1 className="text-xl font-semibold text-gray-900 mb-4">{text.title}</h1>
            <p className="text-gray-600 mb-4">{text.description}</p>
            <button
              type="button"
              onClick={() => (isChunkError ? window.location.reload() : reset())}
              className="w-full bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700"
            >
              {text.tryAgain}
            </button>
          </div>
        </div>
      </body>
    </html>
  );
}
